<template>
  <div
    class="prepeers-accordion-tag-sections"
    :class="{ 'prepeers-accordion-tag-sections--compact': props.density === 'compact' }">
    <div
      v-for="sec in props.sections"
      :key="sec.id"
      class="prepeers-accordion-tag-sections__card">
      <button
        class="prepeers-accordion-tag-sections__header"
        type="button"
        :aria-expanded="isOpen(sec.id)"
        @click="toggleSection(sec.id)">
        <span class="prepeers-accordion-tag-sections__title">{{ sec.title }}</span>
        <span
          v-if="badgeFor(sec.id) > 0"
          class="prepeers-accordion-tag-sections__badge">
          {{ badgeFor(sec.id) }}
        </span>
        <svg
          class="prepeers-accordion-tag-sections__chev"
          :class="{ 'prepeers-accordion-tag-sections__chev--open': isOpen(sec.id) }"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true">
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
      </button>
      <Transition name="pat-acc">
        <div
          v-if="isOpen(sec.id)"
          :key="`${sec.id}-body`"
          class="prepeers-accordion-tag-sections__body">
          <div class="prepeers-accordion-tag-sections__grid">
            <button
              v-for="it in sec.items"
              :key="it.id"
              type="button"
              class="prepeers-accordion-tag-sections__chip-btn"
              @click="onChipClick(sec.id, it.id)">
              <PrePeersTag
                :label="it.label"
                appearance="chip"
                :selected="props.isItemSelected(sec.id, it.id)" />
            </button>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import type { PropType, Ref } from 'vue'

import PrePeersTag from '~/components/core/PrePeersTag.vue'
import type {
  PrePeersAccordionDensity,
  PrePeersAccordionExpandMode,
  PrePeersAccordionTagBadgeCountFn,
  PrePeersAccordionTagIsSelectedFn,
  PrePeersAccordionTagSection,
  PrePeersAccordionTagTogglePayload,
} from '~/types/PrePeersAccordionTagSections'

const props = defineProps({
  sections: {
    type: Array as PropType<readonly PrePeersAccordionTagSection[]>,
    required: true,
  },
  isItemSelected: {
    type: Function as PropType<PrePeersAccordionTagIsSelectedFn>,
    required: true,
  },
  /** Si défini, affiche un compteur dans l'en-tête de section (ex. intérêts sélectionnés par domaine). */
  sectionBadgeCount: {
    type: Function as PropType<PrePeersAccordionTagBadgeCountFn | undefined>,
    default: undefined,
  },
  /** Identifiants de sections ouvertes au montage (ex. premier groupe pour le filtre niveaux). */
  initialOpenSectionIds: {
    type: Array as PropType<readonly string[]>,
    default: (): string[] => [],
  },
  /** `multi` : tunnel intérêts ; `exclusive` : un seul panneau ouvert (filtre niveaux). */
  expandMode: {
    type: String as PropType<PrePeersAccordionExpandMode>,
    default: 'multi',
  },
  /** `compact` : en-tête / corps `var(--space-3) var(--space-4)` (filtre niveaux). */
  density: {
    type: String as PropType<PrePeersAccordionDensity>,
    default: 'default',
  },
})

const emit = defineEmits<{
  'toggle-item': [payload: PrePeersAccordionTagTogglePayload]
}>()

const openSectionIds: Ref<string[]> = ref<string[]>([])

onMounted((): void => {
  if (props.initialOpenSectionIds.length > 0) {
    openSectionIds.value = [...props.initialOpenSectionIds]
  }
})

/**
 * @param {string} sectionId - Identifiant de section.
 * @returns {boolean} true si la section est dépliée.
 */
function isOpen(sectionId: string): boolean {
  return openSectionIds.value.includes(sectionId)
}

/**
 * @param {string} sectionId - Identifiant de section.
 * @returns {number} Compteur badge ou 0.
 */
function badgeFor(sectionId: string): number {
  if (props.sectionBadgeCount === undefined) return 0
  return props.sectionBadgeCount(sectionId)
}

/**
 * @param {string} sectionId - Identifiant de section.
 * @returns {void}
 */
function toggleSection(sectionId: string): void {
  if (props.expandMode === 'exclusive') {
    if (openSectionIds.value.includes(sectionId)) {
      openSectionIds.value = []
    } else {
      openSectionIds.value = [sectionId]
    }
    return
  }

  const idx: number = openSectionIds.value.indexOf(sectionId)
  if (idx >= 0) {
    openSectionIds.value = openSectionIds.value.filter((id: string) => id !== sectionId)
  } else {
    openSectionIds.value = [...openSectionIds.value, sectionId]
  }
}

/**
 * @param {string} sectionId - Section parente.
 * @param {string} itemId - Identifiant de l'item.
 * @returns {void}
 */
function onChipClick(sectionId: string, itemId: string): void {
  emit('toggle-item', { sectionId, itemId })
}
</script>

<style scoped>
.prepeers-accordion-tag-sections {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-top: var(--space-2);
}

.prepeers-accordion-tag-sections__card {
  background: var(--bg-surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: border-color var(--duration-fast) var(--ease-out);
}

.prepeers-accordion-tag-sections__header {
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-5);
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background var(--duration-fast) var(--ease-out);
}

.prepeers-accordion-tag-sections__header:hover {
  background: rgba(255, 255, 255, 0.03);
}

.prepeers-accordion-tag-sections__title {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: var(--text-base);
  color: var(--text-primary);
  flex: 1;
}

.prepeers-accordion-tag-sections__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 var(--space-2);
  background: var(--accent-blue);
  border-radius: var(--radius-full);
  font-size: var(--text-xs);
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.prepeers-accordion-tag-sections__chev {
  color: var(--text-muted);
  flex-shrink: 0;
  transition: transform var(--duration-base) var(--ease-out);
}

.prepeers-accordion-tag-sections__chev--open {
  transform: rotate(180deg);
}

.prepeers-accordion-tag-sections__body {
  padding: var(--space-4) var(--space-5) var(--space-5);
  border-top: 1px solid var(--border-subtle);
}

.prepeers-accordion-tag-sections__grid {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.prepeers-accordion-tag-sections__chip-btn {
  display: inline-block;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  font: inherit;
  cursor: pointer;
}

.prepeers-accordion-tag-sections--compact .prepeers-accordion-tag-sections__header {
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
}

.prepeers-accordion-tag-sections--compact .prepeers-accordion-tag-sections__body {
  padding: var(--space-3) var(--space-4);
}

.prepeers-accordion-tag-sections--compact .prepeers-accordion-tag-sections__title {
  font-size: var(--text-sm);
}

.prepeers-accordion-tag-sections__chip-btn:hover :deep(.prepeers-tag--chip.prepeers-tag--inactive) {
  border-color: var(--accent-blue);
  color: var(--text-primary);
}

.pat-acc-enter-active,
.pat-acc-leave-active {
  transition:
    opacity var(--duration-base) var(--ease-out),
    max-height var(--duration-base) var(--ease-out);
  max-height: 600px;
  overflow: hidden;
}

.pat-acc-enter-from,
.pat-acc-leave-to {
  opacity: 0;
  max-height: 0;
}
</style>
